import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Collapse,
  IconButton,
  Divider,
  Drawer,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import apiClient from '../../../request/http-common';
import { setTradings } from '../../../request/Analysis/hitorySlice';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1E2329',
    color: 'white',
    fontSize: '12px',
    height: '32px',
    '& fieldset': { borderColor: '#2B3139' },
    '&:hover fieldset': { borderColor: '#F7A600' },
    '&.Mui-focused fieldset': { borderColor: '#F7A600' }
  },
  '& .MuiInputLabel-root': { 
    color: '#848E9C', 
    fontSize: '11px',
    '&.Mui-focused': { color: '#F7A600' }
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#1E2329',
  color: 'white',
  fontSize: '12px',
  height: '32px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2B3139' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '& .MuiSvgIcon-root': { color: '#848E9C' },
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const QuickButton = styled(Button)(({ theme }) => ({
  minWidth: '32px',
  height: '24px',
  padding: '2px 8px',
  fontSize: '10px',
  backgroundColor: '#2B3139',
  color: '#848E9C',
  border: '1px solid #2B3139',
  '&:hover': {
    backgroundColor: '#F7A600',
    color: '#000',
    borderColor: '#F7A600'
  }
}));

const Twap = ({ orderType, balance, coinPrice, currentPair, tradingType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tradingPlans = useSelector((state) => state.plans.tradingPlans);
  const currentPairFromState = useSelector((state) => state.currentPair.currentPair);
  const pair = currentPair || currentPairFromState;

  // Form state
  const [runningTimeHours, setRunningTimeHours] = useState(0);
  const [runningTimeMinutes, setRunningTimeMinutes] = useState(30);
  const [frequency, setFrequency] = useState('1M');
  const [quantityPerOrder, setQuantityPerOrder] = useState('');
  const [randomOrder, setRandomOrder] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [quantitySlider, setQuantitySlider] = useState(0);
  const [duration, setDuration] = useState('');
  const [error, setError] = useState('');
  
  // Confirmation drawer state
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingOrderSide, setPendingOrderSide] = useState('');

  // Leverage and margin for futures
  const [leverage, setLeverage] = useState('10');
  const [marginType, setMarginType] = useState('Cross');
  const [postOnly, setPostOnly] = useState(false);
  const [reduceOnly, setReduceOnly] = useState(false);

  const frequencies = ['30S', '1M', '3M', '5M', '10M', '15M', '30M'];
  const quickTimeButtons = ['10m', '30m', '1h', '2h', '4h'];

  // Improved decimal formatting
  const formatPrice = (value) => parseFloat(value || 0).toFixed(2);
  const formatQuantity = (value) => parseFloat(value || 0).toFixed(4);
  const formatBalance = (value) => parseFloat(value || 0).toFixed(2);

  const calculateTotalQuantity = () => {
    const qty = parseFloat(quantityPerOrder || 0);
    const totalTime = (runningTimeHours * 60) + runningTimeMinutes;
    const freqMinutes = frequency === '30S' ? 0.5 : 
                      frequency === '1M' ? 1 :
                      frequency === '3M' ? 3 :
                      frequency === '5M' ? 5 :
                      frequency === '10M' ? 10 :
                      frequency === '15M' ? 15 : 30;
    const orders = Math.floor(totalTime / freqMinutes);
    return formatQuantity(qty * orders);
  };

  const calculateOrderValue = () => {
    const totalQty = parseFloat(calculateTotalQuantity());
    const price = parseFloat(coinPrice || 0);
    return formatPrice(totalQty * price);
  };

  // Update order value when quantity changes
  useEffect(() => {
    // This will trigger re-render when quantityPerOrder changes
  }, [quantityPerOrder]);

  const handleQuickTime = (timeStr) => {
    const minutes = timeStr.includes('m') ? parseInt(timeStr) : parseInt(timeStr) * 60;
    setRunningTimeHours(Math.floor(minutes / 60));
    setRunningTimeMinutes(minutes % 60);
  };

  const handleQuantitySliderChange = (event, newValue) => {
    setQuantitySlider(newValue);
    if (balance && coinPrice) {
      const maxQty = parseFloat(balance) / parseFloat(coinPrice);
      const newQuantity = (maxQty * newValue) / 100;
      setQuantityPerOrder(formatQuantity(newQuantity / 10)); // Divide by estimated number of orders
    }
  };

  const handlePercentageClick = (percentage) => {
    if (balance && coinPrice) {
      const maxQty = parseFloat(balance) / parseFloat(coinPrice);
      const newQuantity = (maxQty * percentage) / 100;
      setQuantityPerOrder(formatQuantity(newQuantity / 10));
      setQuantitySlider(percentage);
    }
  };

  // Submit mutation
  const { isLoading: isSubmitting, mutate: submitOrder } = useMutation(
    async (orderData) => {
      console.log('Submitting TWAP order:', orderData);
      return await apiClient.post(`/api/start-trading`, orderData);
    },
    {
      onSuccess: (res) => {
        console.log('TWAP order response:', res.data);
        if (res.data.status === 'success') {
          dispatch(setTradings(res.data.tradings));
          localStorage.setItem('notification', JSON.stringify({
            message: `${pendingOrderSide === 'buy' ? 'Buy' : 'Sell'} TWAP order placed successfully!`,
            'type': 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          
          // Reset form
          setRunningTimeHours(0);
          setRunningTimeMinutes(30);
          setQuantityPerOrder('');
          setQuantitySlider(0);
          setDuration('');
          setError('');
          setConfirmationOpen(false);
          setPendingOrderSide('');
          
          // Redirect to trade history
          navigate('/trade-history');
        }
      },
      onError: (err) => {
        console.error('TWAP order error:', err);
        if (err.response?.data?.errors) {
          setError(Object.values(err.response.data.errors).flat().join(', '));
        } else {
          let errorMessage = err.response?.data?.message || 'Failed to place TWAP order';
          setError(errorMessage);
          localStorage.setItem('notification', JSON.stringify({
            message: errorMessage,
            'type': 'error'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
        setConfirmationOpen(false);
      },
    }
  );

  const validateOrder = () => {
    if (!quantityPerOrder || parseFloat(quantityPerOrder) <= 0) {
      setError('Please enter a valid quantity per order');
      return false;
    }
    
    if (!duration) {
      setError('Please select a duration');
      return false;
    }

    if (runningTimeHours === 0 && runningTimeMinutes === 0) {
      setError('Please set a valid running time');
      return false;
    }
    
    const totalQty = parseFloat(calculateTotalQuantity());
    const priceNum = parseFloat(coinPrice);
    const orderValueNum = totalQty * priceNum;
    const balanceNum = parseFloat(balance);
    
    if (orderValueNum > balanceNum) {
      setError(`Insufficient balance. Required: ${formatPrice(orderValueNum)} USDT, Available: ${formatBalance(balance)} USDT`);
      return false;
    }
    
    return true;
  };

  const handleOrderClick = (side) => {
    console.log('TWAP order click:', side);
    setError('');
    
    if (!validateOrder()) {
      return;
    }
    
    setPendingOrderSide(side);
    setConfirmationOpen(true);
  };

  const confirmOrder = () => {
    console.log('Confirming TWAP order...');
    
    const orderData = {
      // Basic order fields
      amount: parseFloat(calculateTotalQuantity()) * parseFloat(coinPrice),
      stop_loss: 0,
      take_profit: 0,
      timer_units: parseInt(duration),
      trading_pair: pair,
      order_type: 'TWAP',
      pair: pair,
      entry_point: parseFloat(coinPrice).toFixed(6),
      side: pendingOrderSide,
      
      // Advanced order fields
      trading_type: tradingType || 'Spot',
      running_time_hours: runningTimeHours,
      running_time_minutes: runningTimeMinutes,
      frequency: frequency,
      quantity_per_order: parseFloat(quantityPerOrder),
      random_order: randomOrder,
      post_only: postOnly,
      reduce_only: tradingType === 'Futures' ? reduceOnly : false,
      
      // Futures specific
      leverage: tradingType === 'Futures' ? parseFloat(leverage) : null,
      margin_type: tradingType === 'Futures' ? marginType : null,
    };
    
    console.log('Final TWAP order data:', orderData);
    submitOrder(orderData);
  };

  const getSelectedPlan = () => {
    if (!tradingPlans || !Array.isArray(tradingPlans)) {
      return null;
    }
    return tradingPlans.find(plan => plan.id.toString() === duration.toString());
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: '11px' }}>
          {error}
        </Alert>
      )}

      {/* Available Balance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
          Available
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
          {formatBalance(balance)} USDT
        </Typography>
      </Box>

      {/* Cross/Isolated and Leverage */}
      {tradingType === 'Futures' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <StyledSelect 
            size="small" 
            value={marginType}
            onChange={(e) => setMarginType(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="Cross">Cross</MenuItem>
            <MenuItem value="Isolated">Isolated</MenuItem>
          </StyledSelect>
          <StyledSelect 
            size="small" 
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="10">10x</MenuItem>
            <MenuItem value="20">20x</MenuItem>
            <MenuItem value="50">50x</MenuItem>
            <MenuItem value="100">100x</MenuItem>
          </StyledSelect>
        </Box>
      )}

      {/* Duration Selection */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              Select Duration
            </MenuItem>
            {tradingPlans && Array.isArray(tradingPlans) && tradingPlans.length > 0 && tradingPlans.map(plan => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.duration}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Running Time */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Running Time
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
          <StyledTextField
            size="small"
            value={runningTimeHours}
            onChange={(e) => setRunningTimeHours(parseInt(e.target.value) || 0)}
            placeholder="0"
            sx={{ width: '60px' }}
          />
          <Typography sx={{ color: '#848E9C', fontSize: '11px', alignSelf: 'center' }}>H</Typography>
          <StyledTextField
            size="small"
            value={runningTimeMinutes}
            onChange={(e) => setRunningTimeMinutes(parseInt(e.target.value) || 0)}
            placeholder="30"
            sx={{ width: '60px' }}
          />
          <Typography sx={{ color: '#848E9C', fontSize: '11px', alignSelf: 'center' }}>Min</Typography>
        </Box>
        
        {/* Quick Time Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {quickTimeButtons.map(time => (
            <QuickButton
              key={time}
              onClick={() => handleQuickTime(time)}
            >
              {time}
            </QuickButton>
          ))}
        </Box>
      </Box>

      {/* Frequency */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Frequency
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          >
            {frequencies.map(freq => (
              <MenuItem key={freq} value={freq}>{freq}</MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Quantity per Sub-order */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Quantity per Sub-order ({pair})
        </Typography>
        <StyledTextField
          fullWidth
          size="small"
          value={quantityPerOrder}
          onChange={(e) => setQuantityPerOrder(e.target.value)}
          placeholder="0.0000"
        />
      </Box>

      {/* Quantity Slider */}
      <Box sx={{ mb: 1.5, px: 0.5 }}>
        <Slider
          value={quantitySlider}
          onChange={handleQuantitySliderChange}
          min={0}
          max={100}
          size="small"
          sx={{
            color: '#F7A600',
            height: 4,
            '& .MuiSlider-track': { backgroundColor: '#F7A600' },
            '& .MuiSlider-thumb': { 
              backgroundColor: '#F7A600',
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(247, 166, 0, 0.16)'
              }
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {[0, 25, 50, 75, 100].map(value => (
            <Typography 
              key={value} 
              variant="caption" 
              sx={{ 
                color: '#848E9C', 
                fontSize: '9px',
                cursor: 'pointer',
                '&:hover': { color: '#F7A600' }
              }}
              onClick={() => handlePercentageClick(value)}
            >
              {value}%
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Random Order Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={randomOrder}
            onChange={(e) => setRandomOrder(e.target.checked)}
            size="small"
            sx={{ 
              color: '#848E9C',
              '&.Mui-checked': { color: '#F7A600' },
              padding: '4px'
            }}
          />
        }
        label={
          <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
            Random Order
          </Typography>
        }
        sx={{ mb: 1, ml: 0 }}
      />

      {/* Order Summary */}
      <Box sx={{ 
        bgcolor: '#181A20', 
        borderRadius: 1, 
        p: 1, 
        mb: 1.5,
        border: '1px solid #2B3139'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Total Quantity
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {calculateTotalQuantity()} {pair}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Value
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {calculateOrderValue()} USDT
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Duration
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {getSelectedPlan()?.duration || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Fee
          </Typography>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            {formatPrice(parseFloat(calculateOrderValue()) * 0.001)} USDT
          </Typography>
        </Box>
      </Box>

      {/* Advanced Settings Toggle */}
      <Button
        fullWidth
        onClick={() => setShowAdvanced(!showAdvanced)}
        endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          color: '#848E9C',
          fontSize: '11px',
          justifyContent: 'space-between',
          textTransform: 'none',
          mb: 1
        }}
      >
        Advanced Settings
      </Button>

      {/* Advanced Settings */}
      <Collapse in={showAdvanced}>
        <Box sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={postOnly}
                onChange={(e) => setPostOnly(e.target.checked)}
                size="small"
                sx={{ 
                  color: '#848E9C',
                  '&.Mui-checked': { color: '#F7A600' },
                  padding: '4px'
                }}
              />
            }
            label={
              <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
                Post-Only
              </Typography>
            }
            sx={{ ml: 0, mb: 0.5 }}
          />
          {tradingType === 'Futures' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={reduceOnly}
                  onChange={(e) => setReduceOnly(e.target.checked)}
                  size="small"
                  sx={{ 
                    color: '#848E9C',
                    '&.Mui-checked': { color: '#F7A600' },
                    padding: '4px'
                  }}
                />
              }
              label={
                <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
                  Reduce-Only
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          )}
        </Box>
      </Collapse>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('buy')}
          disabled={isSubmitting}
          sx={{
            bgcolor: orderType === 'Buy' ? '#0ECB81' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Buy' ? '#0BB76D' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? 'Processing...' : `${tradingType === 'Futures' ? 'Long' : 'Buy'} ${pair}`}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('sell')}
          disabled={isSubmitting}
          sx={{
            bgcolor: orderType === 'Sell' ? '#F6465D' : '#1E2329',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: orderType === 'Sell' ? '#E13B52' : '#2A2E39'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? 'Processing...' : `${tradingType === 'Futures' ? 'Short' : 'Sell'} ${pair}`}
        </Button>
      </Box>

      {/* Confirmation Drawer */}
      <Drawer
        anchor="bottom"
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#181A20',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            zIndex: 99999,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
          }
        }}
        ModalProps={{
          sx: {
            zIndex: 99999,
          }
        }}
        sx={{
          zIndex: 99999,
          '& .MuiDrawer-paper': {
            zIndex: 99999,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
            Confirm TWAP Order
          </Typography>
          
          <Box sx={{ bgcolor: '#1E2329', borderRadius: 1, p: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Order Type:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                TWAP
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Side:</Typography>
              <Typography variant="body2" sx={{ 
                color: pendingOrderSide === 'buy' ? '#0ECB81' : '#F6465D', 
                fontWeight: 500 
              }}>
                {pendingOrderSide === 'buy' ? 'Buy' : 'Sell'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Pair:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {pair}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Running Time:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {runningTimeHours}h {runningTimeMinutes}m
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Frequency:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {frequency}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Qty per Order:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {quantityPerOrder} {pair}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Total Quantity:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {calculateTotalQuantity()} {pair}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Order Value:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {calculateOrderValue()} USDT
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Duration:</Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {getSelectedPlan()?.duration || 'N/A'}
              </Typography>
            </Box>
            <Divider sx={{ my: 1, borderColor: '#2B3139' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>Est. Fee:</Typography>
              <Typography variant="body2" sx={{ color: '#848E9C' }}>
                {formatPrice(parseFloat(calculateOrderValue()) * 0.001)} USDT
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setConfirmationOpen(false)}
              disabled={isSubmitting}
              sx={{
                borderColor: '#2B3139',
                color: '#848E9C',
                '&:hover': {
                  borderColor: '#F7A600',
                  color: '#F7A600'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={confirmOrder}
              disabled={isSubmitting}
              sx={{
                bgcolor: '#F7A600',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#E69500'
                },
                '&:disabled': {
                  bgcolor: '#2B3139',
                  color: '#848E9C'
                }
              }}
            >
              {isSubmitting ? 'Processing...' : 'Confirm Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Twap;
